'use client';

import { useTranslations } from 'next-intl';
import { Link, useLocale } from '@/i18n/routing';
import type { EarnActivity } from '../../_apis';
import { useEarnStatus } from '../../_hooks/use-earn-status';
import { getEarnLocalizedText } from '../../_localized';
import { getEarnActivityStatus } from '../../_utils';

type LandingActivityCardProps = {
  activity: EarnActivity;
};

export function LandingActivityCard({ activity }: LandingActivityCardProps) {
  const t = useTranslations('earn');
  const locale = useLocale();
  const { status: applyStatus } = useEarnStatus();
  const canViewDetail = applyStatus === 'approved';
  const href = activity.id ? `/earn/activities/${activity.id}` : '/earn/activities';
  const title = getEarnLocalizedText(activity.title, locale);
  const content = getEarnLocalizedText(activity.content, locale);
  const status = getEarnActivityStatus(activity);
  const statusText: Record<typeof status, string> = {
    ended: t('activity.ended'),
    in_progress: t('activity.inProgress'),
    not_started: t('activity.notStarted'),
  };
  const statusTextClassName: Record<typeof status, string> = {
    ended: 'text-system-white48',
    in_progress: 'text-[#0086FF]',
    not_started: 'text-system-white',
  };
  const card = (
    <article className="flex min-h-[292px] flex-col rounded-[22px] bg-[#1d1e20]/95 p-6 shadow-[0_28px_80px_rgba(0,0,0,0.28)] transition duration-300 group-hover:bg-[#222326] sm:min-h-0 sm:p-4">
      <div className="flex flex-col gap-4">
        <span
          className={`w-fit shrink-0 rounded-full bg-white/[0.08] px-3 py-1 font-medium text-base leading-6 sm:text-sm ${statusTextClassName[status]}`}
        >
          {statusText[status]}
        </span>
        <h3 className="line-clamp-1 font-semibold text-[21px] text-white leading-snug sm:text-lg">{title}</h3>
      </div>

      {content && (
        <p className="mt-5 line-clamp-4 whitespace-pre-line text-base text-system-white64 leading-6">{content}</p>
      )}
    </article>
  );

  return canViewDetail ? (
    <Link href={href} className="group block min-w-0">
      {card}
    </Link>
  ) : (
    card
  );
}
